/**
 * Summit Air Web Push helper. Wraps navigator.pushManager subscribe/unsubscribe
 * and syncs with the backend so approvers get a home-screen alert when an
 * engineer submits their timesheet for approval.
 *
 * iOS caveat: Web Push works on iOS 16.4+ ONLY when the app is running in
 * standalone mode (installed to Home Screen). We surface that requirement in
 * the Profile page (see `pushCapability()` below).
 */
import { api } from "@/lib/api";
import { isStandaloneMode, isIos } from "@/lib/pwa";

// Convert a base64url string (VAPID public key) into a Uint8Array for the
// browser's pushManager.subscribe({ applicationServerKey }) call.
function base64UrlToUint8Array(base64Url) {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * What the current browser can / cannot do. Return codes:
 *   supported       — full Web Push works right now
 *   ios-not-installed — iOS user hasn't installed to home screen yet
 *   unsupported     — browser lacks Notification / PushManager entirely
 */
export function pushCapability() {
  if (typeof window === "undefined") return "unsupported";
  const hasNotification = "Notification" in window;
  const hasPushManager = "PushManager" in window;
  const hasSw = "serviceWorker" in navigator;
  if (!hasNotification || !hasPushManager || !hasSw) {
    if (isIos() && !isStandaloneMode()) return "ios-not-installed";
    return "unsupported";
  }
  if (isIos() && !isStandaloneMode()) return "ios-not-installed";
  return "supported";
}

/** Current permission state: 'granted' | 'denied' | 'default' | 'unsupported'. */
export function currentPushPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/**
 * Prompt the browser for permission, then subscribe against the backend's
 * VAPID public key. Returns the subscription object on success. Throws on
 * denial or transport error.
 */
export async function subscribeToPush() {
  const cap = pushCapability();
  if (cap !== "supported") {
    const msg = cap === "ios-not-installed"
      ? "Install SA to your Home Screen first, then enable notifications from there."
      : "Push notifications aren't supported on this browser.";
    throw new Error(msg);
  }
  // Ask permission
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error(perm === "denied" ? "Permission denied." : "Permission dismissed.");

  const reg = await navigator.serviceWorker.getRegistration("/") || await navigator.serviceWorker.ready;
  if (!reg) throw new Error("Service worker not ready yet — reload and try again.");

  // Fetch the VAPID public key from the backend
  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = base64UrlToUint8Array(data.public_key);

  // Subscribe against the push service
  let subscription = await reg.pushManager.getSubscription();
  if (subscription) {
    // Recycle — key rotation edge case: subscribed with an old key.
    try { await subscription.unsubscribe(); } catch { /* best-effort */ }
    subscription = null;
  }
  subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  // Persist to the backend
  const raw = subscription.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent || null,
  });
  return subscription;
}

/**
 * Undo local + server subscription for this device. Idempotent — safe to call
 * even if the user never opted in.
 */
export async function unsubscribeFromPush() {
  const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration("/") : null;
  const subscription = reg ? await reg.pushManager.getSubscription() : null;
  if (subscription) {
    const endpoint = subscription.endpoint;
    try { await subscription.unsubscribe(); } catch { /* best-effort */ }
    try { await api.delete("/push/subscribe", { data: { endpoint } }); } catch { /* endpoint may already be gone */ }
  }
}

/**
 * Returns true if the current browser is currently subscribed on this device.
 * Also cross-checks with the backend — if the browser thinks it's subscribed
 * but the server lost the subscription (e.g. DB wipe), returns false.
 */
export async function isCurrentlySubscribed() {
  if (pushCapability() !== "supported") return false;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    const { data } = await api.get("/push/my-subscriptions");
    return Array.isArray(data) && data.some((s) => s.endpoint === sub.endpoint);
  } catch {
    return true;  // couldn't reach server — assume subscribed
  }
}
